import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight, Play, Pause } from "lucide-react";
import { MediaGate } from "@/components/Consent";
import { useLang } from "@/context/LanguageContext";

export const EASE = [0.22, 1, 0.36, 1];

// Titles/meta are managed centrally by <Seo /> (components/Seo.jsx); kept for API compatibility.
export const usePageTitle = () => {};

const isFinePointer = () =>
  typeof window !== "undefined" && window.matchMedia("(pointer: fine)").matches;

/* ---------------- Reveal ---------------- */
export const Reveal = ({ children, delay = 0, y = 40, className = "", immediate = false, duration = 0.9 }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    {...(immediate ? { animate: { opacity: 1, y: 0 } } : { whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" } })}
    transition={{ duration, delay, ease: EASE }}
    className={className}
  >
    {children}
  </motion.div>
);

/* ---------------- Labels ---------------- */
export const ChapterLabel = ({ no, label }) => (
  <div className="mb-6 flex items-center gap-4">
    {no && <span className="font-mono text-xs tracking-[0.3em] text-gold">{no}</span>}
    <motion.span
      initial={{ scaleX: 0 }}
      whileInView={{ scaleX: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 1, ease: EASE }}
      className="h-px w-10 origin-left bg-[#C5A059]/40"
    />
    <h2 className="font-sans text-xs font-medium uppercase tracking-[0.3em] text-[#A1A1AA]">{label}</h2>
  </div>
);

export const ChapterLabelCenter = ({ no, label }) => (
  <div className="mb-6 flex items-center justify-center gap-4">
    {no && <span className="font-mono text-xs tracking-[0.3em] text-gold">{no}</span>}
    <span className="h-px w-10 bg-[#C5A059]/40" />
    <h2 className="font-sans text-xs font-medium uppercase tracking-[0.3em] text-[#A1A1AA]">{label}</h2>
    <span className="h-px w-10 bg-[#C5A059]/40" />
  </div>
);

/* ---------------- Mask Reveal (line-based) ---------------- */
export const MaskLines = ({ text, className = "", as: Tag = "h2", delay = 0 }) => (
  <Tag className={className}>
    {text.split("\n").map((line, i) => (
      <motion.span
        key={i}
        className="block overflow-hidden pb-1"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-40px" }}
      >
        <motion.span
          className="block"
          variants={{ hidden: { y: "110%" }, visible: { y: 0 } }}
          transition={{ duration: 1, delay: delay + i * 0.12, ease: EASE }}
        >
          {line}
        </motion.span>
      </motion.span>
    ))}
  </Tag>
);

/* ---------------- Staggered character reveal ---------------- */
export const StaggerTitle = ({ text, className = "", lineClass = "", delay = 0 }) => (
  <motion.span
    className={`block ${className}`}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-40px" }}
  >
    {text.split("\n").map((line, li) => (
      <span key={li} className={`block overflow-hidden pb-1 ${lineClass}`}>
        <span className="block whitespace-nowrap">
          {line.split(" ").map((word, wi) => (
            <span key={wi} className="inline-block whitespace-nowrap">
              {word.split("").map((ch, ci) => (
                <motion.span
                  key={ci}
                  className="inline-block"
                  variants={{ hidden: { y: "115%", rotate: 4 }, visible: { y: 0, rotate: 0 } }}
                  transition={{ duration: 0.85, ease: EASE, delay: delay + li * 0.18 + (wi * 3 + ci) * 0.02 }}
                >
                  {ch}
                </motion.span>
              ))}
              {wi < line.split(" ").length - 1 && <span className="inline-block">&nbsp;</span>}
            </span>
          ))}
        </span>
      </span>
    ))}
  </motion.span>
);

/* ---------------- Magnetic wrapper ---------------- */
export const Magnetic = ({ children, strength = 0.35, className = "" }) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 160, damping: 14, mass: 0.25 });
  const sy = useSpring(y, { stiffness: 160, damping: 14, mass: 0.25 });
  const onMove = (e) => {
    if (!isFinePointer()) return;
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - (r.left + r.width / 2)) * strength);
    y.set((e.clientY - (r.top + r.height / 2)) * strength);
  };
  const onLeave = () => {
    x.set(0);
    y.set(0);
  };
  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ x: sx, y: sy }}
      className={`inline-block ${className}`}
    >
      {children}
    </motion.div>
  );
};

/* ---------------- CTA (magnetic) ---------------- */
export const CTALink = ({ to, children, testid, variant = "solid", className = "", onClick, cursor = "VIEW" }) => {
  const base =
    "group relative inline-flex items-center gap-3 overflow-hidden px-8 py-4 font-sans text-[11px] font-medium uppercase tracking-[0.3em] transition-colors duration-500";
  const styles =
    variant === "solid"
      ? "bg-[#C5A059] text-[#0B0B0C] hover:bg-[#E5C158]"
      : "border border-white/20 text-[#F9F8F6] hover:border-[#C5A059] hover:text-[#C5A059]";
  const inner = (
    <>
      <span>{children}</span>
      <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
    </>
  );
  const el = to ? (
    <Link to={to} data-testid={testid} data-cursor={cursor} onClick={onClick} className={`${base} ${styles} ${className}`}>
      {inner}
    </Link>
  ) : (
    <button type="button" data-testid={testid} data-cursor={cursor} onClick={onClick} className={`${base} ${styles} ${className}`}>
      {inner}
    </button>
  );
  return <Magnetic strength={0.25}>{el}</Magnetic>;
};

/* ---------------- Parallax Image ---------------- */
export const ParallaxImage = ({ src, alt = "", className = "", speed = 60, imgClass = "", bw = false }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-speed, speed]);
  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`}>
      <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        style={{ y, scale: 1.06 }}
        className={`h-full w-full object-cover ${bw ? "img-bw" : ""} ${imgClass}`}
      />
    </div>
  );
};

/* ---------------- Clip-path Image Reveal ---------------- */
export const ImageReveal = ({ src, alt = "", className = "", imgClass = "", bw = false, delay = 0, hoverZoom = true, children }) => (
  <motion.div
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-60px" }}
    className={`group/ir ${className}`}
  >
    <motion.div
      variants={{ hidden: { clipPath: "inset(100% 0 0 0)" }, visible: { clipPath: "inset(0% 0 0 0)" } }}
      transition={{ duration: 1.15, delay, ease: EASE }}
      className="relative h-full w-full overflow-hidden"
    >
      <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        variants={{ hidden: { scale: 1.28 }, visible: { scale: 1 } }}
        whileHover={hoverZoom ? { scale: 1.06 } : undefined}
        transition={{ duration: 1.5, delay, ease: EASE }}
        className={`h-full w-full object-cover ${bw ? "img-bw" : ""} ${imgClass}`}
      />
      {children}
    </motion.div>
  </motion.div>
);

/* ---------------- Horizontal scroll gallery ---------------- */
export const HorizontalGallery = ({ images, height = "h-[48vh] sm:h-[56vh]", width = "w-[76vw] sm:w-[44vw] lg:w-[30vw]" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const x = useTransform(scrollYProgress, [0, 1], ["3%", "-24%"]);
  return (
    <div ref={ref} className="relative overflow-hidden">
      <motion.div style={{ x }} className="flex w-max gap-5">
        {images.map((g, i) => (
          <div key={`${g}-${i}`} data-cursor="VIEW" className={`group relative shrink-0 overflow-hidden ${height} ${width}`}>
            <img
              src={g}
              alt=""
              loading="lazy"
              className={`h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${i % 2 === 1 ? "img-bw" : ""}`}
            />
          </div>
        ))}
      </motion.div>
    </div>
  );
};

/* ---------------- 3D Tilt ---------------- */
export const Tilt = ({ children, className = "", max = 8 }) => {
  const ref = useRef(null);
  const [transform, setTransform] = useState("perspective(1100px)");
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTransform(`perspective(1100px) rotateY(${px * max}deg) rotateX(${-py * max}deg) scale(1.02)`);
  };
  const onLeave = () => setTransform("perspective(1100px) rotateY(0deg) rotateX(0deg) scale(1)");
  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ transform, transition: "transform 0.5s cubic-bezier(0.22,1,0.36,1)", transformStyle: "preserve-3d" }}
      className={className}
    >
      {children}
    </div>
  );
};

/* ---------------- Marquee ---------------- */
export const Marquee = ({ items }) => {
  const row = [...items, ...items];
  return (
    <div className="overflow-hidden border-y border-white/5 py-6">
      <div className="animate-marquee flex w-max items-center gap-12">
        {row.map((item, i) => (
          <span key={i} className="flex items-center gap-12 whitespace-nowrap">
            <span className="font-serif text-2xl font-light italic text-[#F9F8F6]/60 sm:text-4xl">{item}</span>
            <span className="h-1.5 w-1.5 rotate-45 bg-[#C5A059]/50" />
          </span>
        ))}
      </div>
    </div>
  );
};

/* ---------------- Cursor-following image preview ---------------- */
export const CursorFollowImage = ({ img }) => {
  const [enabled, setEnabled] = useState(false);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 140, damping: 20, mass: 0.3 });
  const sy = useSpring(y, { stiffness: 140, damping: 20, mass: 0.3 });
  useEffect(() => {
    if (!isFinePointer()) return;
    setEnabled(true);
    const move = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
    };
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, [x, y]);
  if (!enabled) return null;
  return (
    <motion.div
      data-testid="cursor-follow-image"
      className="pointer-events-none fixed left-0 top-0 z-40 hidden lg:block"
      style={{ x: sx, y: sy, marginLeft: 40, marginTop: -140 }}
    >
      <AnimatePresence mode="wait">
        {img && (
          <motion.img
            key={img}
            src={img}
            alt=""
            initial={{ opacity: 0, scale: 0.85, rotate: -3 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="h-[280px] w-[210px] object-cover shadow-2xl shadow-black/70"
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ---------------- Fake Audio Player (visual) ---------------- */
export const FakePlayer = ({ src, links = [] }) => {
  const { t } = useLang();
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const toggle = () => {
    const a = audioRef.current;
    if (a) {
      if (playing) a.pause();
      else a.play().catch(() => {});
    }
    setPlaying(!playing);
  };
  if (!src) {
    const link = links.find((l) => l.label === "Spotify") || links[0];
    if (!link) return null;
    return (
      <a
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="featured-listen-link"
        data-cursor="PLAY"
        className="group flex items-center gap-5 border border-white/10 bg-[#0B0B0C]/60 px-5 py-4 backdrop-blur transition-colors duration-300 hover:border-[#C5A059]/50"
      >
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#C5A059]/50 text-[#C5A059] transition-all duration-300 group-hover:bg-[#C5A059] group-hover:text-[#0B0B0C]">
          <Play className="ml-0.5 h-4 w-4" />
        </span>
        <span className="flex-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#F9F8F6]">{t.common.listenNow}</span>
        <span className="font-mono text-[10px] tracking-[0.2em] text-[#71717A]">{link.label} ↗</span>
      </a>
    );
  }
  return (
    <div className="flex items-center gap-5 border border-white/10 bg-[#0B0B0C]/60 px-5 py-4 backdrop-blur">
      {src && <audio ref={audioRef} src={src} preload="none" onEnded={() => setPlaying(false)} />}
      <button
        data-testid="hero-audio-play-button"
        data-cursor="PLAY"
        onClick={toggle}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#C5A059]/50 text-[#C5A059] transition-all duration-300 hover:bg-[#C5A059] hover:text-[#0B0B0C]"
        aria-label="Play"
      >
        {playing ? <Pause className="h-4 w-4" /> : <Play className="ml-0.5 h-4 w-4" />}
      </button>
      <div className="flex h-8 flex-1 items-end gap-[3px]">
        {Array.from({ length: 36 }).map((_, i) => (
          <span
            key={i}
            className={`w-[3px] bg-[#C5A059]/70 ${playing ? "eq-bar" : ""}`}
            style={{ height: playing ? undefined : "20%", animationDelay: `${(i % 8) * 0.11}s` }}
          />
        ))}
      </div>
      <span className="hidden font-mono text-[10px] tracking-[0.2em] text-[#71717A] sm:block">
        {playing ? "PLAYING" : t.common.listenNow}
      </span>
    </div>
  );
};

/* ---------------- Page Hero (oversized editorial) ---------------- */
export const PageHero = ({ eyebrow, title, img, children, bw = false, imgClass = "" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.1]);
  const lines = title.split("\n");
  return (
    <section ref={ref} className="relative flex min-h-[95vh] items-end overflow-hidden">
      <motion.div style={{ y, scale, opacity }} className="absolute inset-0">
        <img fetchPriority="high" decoding="async" src={img} alt={typeof title === "string" ? title.replace(/\n/g, " ") : ""} className={`h-full w-full object-cover opacity-40 ${bw ? "img-bw" : ""} ${imgClass}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-[#0B0B0C]/30 to-[#0B0B0C]/70" />
      </motion.div>
      <div className="relative z-10 w-full px-6 pb-14 pt-44 sm:px-12 lg:px-20">
        <Reveal y={16}>
          <p className="mb-8 flex items-center gap-4 font-sans text-[11px] font-medium uppercase tracking-[0.4em] text-gold">
            <span className="h-px w-12 bg-[#C5A059]/50" />
            {eyebrow}
          </p>
        </Reveal>
        <h1 className="font-serif font-light leading-[0.88] tracking-tight">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-2">
              <motion.span
                className={`block text-[14vw] leading-[0.9] sm:text-[11.5vw] lg:text-[9.5vw] ${
                  i > 0 ? "text-outline" : "text-[#F9F8F6]"
                } ${i === 1 ? "lg:ml-[7vw]" : ""}`}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.25, delay: 0.2 + i * 0.15, ease: EASE }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
        {children && <div className="mt-10 max-w-xl">{children}</div>}
      </div>
      <motion.span
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1.4, delay: 0.9, ease: EASE }}
        className="absolute bottom-0 left-0 h-px w-full origin-left bg-gradient-to-r from-[#C5A059]/60 via-white/10 to-transparent"
      />
    </section>
  );
};

/* ---------------- Booking closer (overflowing type) ---------------- */
export const BookingSection = () => {
  const { t, lp } = useLang();
  return (
    <section data-testid="booking-section" className="relative overflow-hidden py-32 sm:py-44">
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.05]">
        <span className="whitespace-nowrap font-serif text-[26vw] leading-none text-outline-strong">{t.home.bookingLabel}</span>
      </div>
      <div className="relative z-10 px-6 text-center sm:px-12">
        <Reveal>
          <ChapterLabelCenter no={t.home.bookingNo} label={t.home.bookingLabel} />
        </Reveal>
        <MaskLines
          text={t.home.bookingTitle}
          className="mx-auto font-serif text-[11vw] font-light leading-[0.95] tracking-tight text-[#F9F8F6] sm:text-[7vw] lg:text-[5.5vw]"
        />
        <Reveal delay={0.35} className="mt-14">
          <CTALink to={lp("/contact")} testid="booking-cta-button">{t.common.booking}</CTALink>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------------- Video Lightbox ---------------- */
export const VideoLightbox = ({ item, onClose }) => {
  if (!item) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[95] flex items-center justify-center bg-[#0B0B0C]/95 p-6 backdrop-blur-sm"
      onClick={onClose}
      data-testid="video-lightbox"
    >
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.4, ease: EASE }}
        className="relative w-full max-w-5xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative aspect-video overflow-hidden bg-black">
          {item.youtubeId ? (
            <MediaGate poster={item.img} title={item.title}>
              <iframe
                data-testid="video-lightbox-iframe"
                src={`https://www.youtube-nocookie.com/embed/${item.youtubeId}?rel=0&color=white`}
                title={item.title}
                className="h-full w-full"
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </MediaGate>
          ) : (
            <>
              <img loading="lazy" decoding="async" src={item.img} alt={item.title} className="h-full w-full object-cover" />
              <div className="absolute inset-0 flex items-center justify-center bg-[#0B0B0C]/40">
                <div className="flex h-20 w-20 items-center justify-center rounded-full border border-[#C5A059]/60 bg-[#0B0B0C]/60 backdrop-blur">
                  <span className="ml-1 inline-block border-y-[10px] border-l-[16px] border-y-transparent border-l-[#C5A059]" />
                </div>
              </div>
            </>
          )}
        </div>
        <div className="mt-4 flex items-center justify-between">
          <p className="font-serif text-xl font-light text-[#F9F8F6]">{item.title}</p>
          <button
            data-testid="video-lightbox-close-button"
            onClick={onClose}
            className="font-mono text-xs tracking-[0.3em] text-[#A1A1AA] transition-colors hover:text-[#C5A059]"
          >
            CLOSE
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};
